import type { WasteFormSchema } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type PortServicesDocument = {
  name: string;
  type?: string;
  documentType?: string;
  otherSpecifyType?: string;
  documentSummary?: string;
  url: string;
  certificateType?: string;
  originalFileName?: string;
  issueDate?: Date | string | null;
  expirationDate?: Date | string | null;
};
export type PortServicesDocumentWithExpirationDate = PortServicesDocument & {
  expirationDate: Date | string | null;
};

export type MarpolAnnexI = {
  id?: string;
  marpolAnnexIOil: string;
  otherSpecifyDetails: string;
  quantityM3: number;
};

export type Resource = {
  resourceTypeCode: string;
  resourceTypeName: string;
  numberOfResources: number;
  usageStartTime: string;
  usageEndTime: string;
};

export type ShipChandlingAdditionalResource = {
  resourceType: string;
  resourceTypeLabel?: string;
  numberOfResources: string;
};

export interface PortServicesFormData {
  serviceCategory: string;
  serviceCategoryCode: string;
  serviceType: string;
  serviceTypeCode: string;
  serviceSubType: string;
  serviceSubTypeCode: string;
  quantity: string;
  unitOfMeasurement: string;
  numberOfSkips: string;
  direction: string;
  numberOfPackagesHandled: string;
  additionalResources: ShipChandlingAdditionalResource[];
  receivingVesselImoNumber: string;
  receivingVesselId: string;
  receivingVesselName: string;
  receivingVesselCallId: string;
  receivingVesselCallNumber: string;
  receivingVesselLookupPending: boolean;
  customsDocuments: PortServicesDocumentWithExpirationDate[];
  garbageCategory?: string;
  typeOfSludge?: string;
  serviceLocation?: string;
  rts: string | Date;
  description?: string | undefined;
  remarks?: string | undefined;
  attachmentDocuments?: PortServicesDocumentWithExpirationDate[];
  garbageDocument?: PortServicesDocumentWithExpirationDate[];
  slopsDocument?: PortServicesDocumentWithExpirationDate[];
  sludgeDocument?: PortServicesDocumentWithExpirationDate[];
  debunkeringDocument?: PortServicesDocumentWithExpirationDate[];
  newChatMessage: string;
  isTypeMandatory: boolean;
  isSubtypeMandatory: boolean;
  registeredAgentName: string;
  registeredAgentContactPerson: string;
  serviceProviderName: string;
  operationLocation: string;
  intendedOperationDetails: string;
  otherVesselImoNumber: string;
  otherVesselId: string;
  otherVesselName: string;
  otherVesselCallId: string;
  otherVesselCallNumber: string;
  sireReportIssueDate: string | Date | null;
  hoseInspectionDate: string | Date | null;
  sireReportDocuments: PortServicesDocumentWithExpirationDate[];
  billOfLadingDocuments: PortServicesDocumentWithExpirationDate[];
  cargoManifestDocuments: PortServicesDocumentWithExpirationDate[];
  certificateOfOriginDocuments: PortServicesDocumentWithExpirationDate[];
  hoseCertificateDocuments: PortServicesDocumentWithExpirationDate[];
  otherDocuments: PortServicesDocumentWithExpirationDate[];
  wasteResiduesForDischargeFacility: MarpolAnnexI[];
  addWasteDraft: Partial<WasteFormSchema> | null;
  bunkeringType: string;
  berthOrAnchorageCode: string;
  bunkeringCompanyName: string;
  bunkerBargeIMO: string;
  bunkerBargeName: string;
  bunkerBargeVesselCallId: string;
  bunkerBargeLookupPending: boolean;
  bunkerBargeLookupErrorMessageKey: string;
  deSlopingType: string;
  deSlopingCompanyName: string;
}

export const defaultValues: PortServicesFormData = {
  serviceCategory: "",
  serviceCategoryCode: "",
  serviceType: "",
  serviceTypeCode: "",
  serviceSubType: "",
  serviceSubTypeCode: "",
  direction: "",
  numberOfPackagesHandled: "",
  additionalResources: [],
  receivingVesselImoNumber: "",
  receivingVesselId: "",
  receivingVesselName: "",
  receivingVesselCallId: "",
  receivingVesselCallNumber: "",
  receivingVesselLookupPending: false,
  customsDocuments: [] as PortServicesDocumentWithExpirationDate[],
  registeredAgentName: "",
  registeredAgentContactPerson: "",
  serviceProviderName: "",
  operationLocation: "",
  intendedOperationDetails: "",
  otherVesselImoNumber: "",
  otherVesselId: "",
  otherVesselName: "",
  otherVesselCallId: "",
  otherVesselCallNumber: "",
  sireReportIssueDate: null,
  hoseInspectionDate: null,
  sireReportDocuments: [] as PortServicesDocumentWithExpirationDate[],
  billOfLadingDocuments: [] as PortServicesDocumentWithExpirationDate[],
  cargoManifestDocuments: [] as PortServicesDocumentWithExpirationDate[],
  certificateOfOriginDocuments: [] as PortServicesDocumentWithExpirationDate[],
  hoseCertificateDocuments: [] as PortServicesDocumentWithExpirationDate[],
  otherDocuments: [] as PortServicesDocumentWithExpirationDate[],
  wasteResiduesForDischargeFacility: [],
  addWasteDraft: null,
  // Bunkering specific fields
  bunkeringType: "",
  berthOrAnchorageCode: "",
  bunkeringCompanyName: "",
  bunkerBargeIMO: "",
  bunkerBargeName: "",
  bunkerBargeVesselCallId: "",
  bunkerBargeLookupPending: false,
  bunkerBargeLookupErrorMessageKey: "",
  // De-sloping specific fields
  deSlopingType: "",
  deSlopingCompanyName: "",
  // Waste disposal specific fields
  quantity: "",
  unitOfMeasurement: "",
  numberOfSkips: "",
  garbageCategory: "",
  typeOfSludge: "",
  serviceLocation: "",
  rts: "",
  description: "",
  remarks: "",
  attachmentDocuments: [] as PortServicesDocumentWithExpirationDate[],
  garbageDocument: [] as PortServicesDocumentWithExpirationDate[],
  slopsDocument: [] as PortServicesDocumentWithExpirationDate[],
  sludgeDocument: [] as PortServicesDocumentWithExpirationDate[],
  debunkeringDocument: [] as PortServicesDocumentWithExpirationDate[],
  newChatMessage: "", // this is used for persisting the chat message
  isTypeMandatory: true,
  isSubtypeMandatory: true,
};

export interface PortServicesFormState extends PortServicesFormData {
  setField<K extends keyof PortServicesFormData>(key: K, val: PortServicesFormData[K]): void;
  getField<K extends keyof PortServicesFormData>(key: K): PortServicesFormData[K];
  setAll(data: Partial<PortServicesFormData>): void;
  resetForm(): void;
  setAddWasteDraft: (draft: Partial<WasteFormSchema> | null) => void;
  clearAddWasteDraft: () => void;
  addWaste: (waste: MarpolAnnexI) => void;
  setWaste: (wastes: MarpolAnnexI[]) => void;
}

export const usePortServicesFormStore = create<PortServicesFormState>()(
  persist(
    (set, get) => ({
      ...defaultValues,
      setField: <K extends keyof PortServicesFormData>(key: K, val: PortServicesFormData[K]) =>
        set((state) => ({ ...state, [key]: val })),
      getField: (key) => get()[key],
      setAll: (data: Partial<PortServicesFormData>) => set((state) => ({ ...state, ...data })),
      resetForm: () => set(() => ({ ...defaultValues })),
      addWaste: (waste) =>
        set((s) => ({
          ...s,
          wasteResiduesForDischargeFacility: [...s.wasteResiduesForDischargeFacility, waste],
        })),
      setWaste: (wastes) =>
        set((s) => ({
          ...s,
          wasteResiduesForDischargeFacility: wastes,
        })),
      setAddWasteDraft: (draft) =>
        set((s) => {
          const currentDraft = JSON.stringify(s.addWasteDraft ?? null);
          const nextDraft = JSON.stringify(draft ?? null);

          if (currentDraft === nextDraft) {
            return s;
          }

          return {
            ...s,
            addWasteDraft: draft,
          };
        }),
      clearAddWasteDraft: () =>
        set((s) => ({
          ...s,
          addWasteDraft: null,
        })),
    }),
    { name: "request-port-services-form" },
  ),
);
